/**
 * Hoffmaster Carousel Widget Handler
 * Initializes Swiper.js carousels with simplified configuration.
 * Handles both frontend page load and Elementor editor live preview.
 */

class HoffmasterCarouselHandler extends elementorModules.frontend.handlers.Base {
	getDefaultSettings() {
		return {
			selectors: {
				wrapper: '.hm-carousel',
				swiperContainer: '.hm-carousel__swiper',
				card: '.hm-carousel__card',
				pagination: '.hm-carousel__pagination',
				prevArrow: '.hm-carousel__arrow--prev',
				nextArrow: '.hm-carousel__arrow--next',
				cardInfoSection: '.hm-carousel__cta .hm-carousel__card-info',
			},
		};
	}

	getDefaultElements() {
		const selectors = this.getSettings('selectors');
		return {
			$wrapper: this.$element.find(selectors.wrapper),
			$swiperContainer: this.$element.find(selectors.swiperContainer),
			$cards: this.$element.find(selectors.card),
			$pagination: this.$element.find(selectors.pagination),
			$prevArrow: this.$element.find(selectors.prevArrow),
			$nextArrow: this.$element.find(selectors.nextArrow),
			$cardInfoSection: this.$element.find(selectors.cardInfoSection),
		};
	}

	bindEvents() {
		if (!this.elements.$wrapper.length || !this.elements.$swiperContainer.length) {
			return;
		}

		// Destroy existing Swiper instance if re-initializing
		const swiperContainer = this.elements.$swiperContainer[0];
		if (swiperContainer.swiper) {
			swiperContainer.swiper.destroy(true, true);
		}

		// Check if we're in preview mode by looking at URL
		const isPreviewMode = window.location.search.includes('preview=true');

		if (isPreviewMode) {
			// In preview mode, wait for container to have valid dimensions
			this.waitForValidDimensions().then(() => {
				this.initCarousel();
			});
		} else {
			this.initCarousel();
		}
	}

	waitForValidDimensions() {
		return new Promise((resolve) => {
			const wrapper = this.elements.$wrapper[0];
			const checkDimensions = () => {
				const width = wrapper.offsetWidth;
				// Valid dimension check: width should be > 100px (reasonable minimum)
				if (width > 100) {
					resolve();
				} else {
					// Use requestAnimationFrame for smoother checking
					requestAnimationFrame(checkDimensions);
				}
			};

			// Start checking after a small delay to allow layout to settle
			setTimeout(checkDimensions, 50);
		});
	}

	initCarousel() {
		const wrapper = this.elements.$wrapper[0];
		const swiperContainer = this.elements.$swiperContainer[0];

		// Check if this is quote-1 style (no pagination)
		this.isQuote1 = this.elements.$wrapper.hasClass('hm-carousel--quote-1');

		// Get sizing mode
		this.sizingMode = wrapper.dataset.sizingMode || '';

		// Gap value
		this.gap = 24;

		let currentSlidesPerView;
		let currentCardWidth;

		if (this.sizingMode === 'card_width') {
			// CARD WIDTH MODE
			const cardWidthWidescreen = parseInt(wrapper.dataset.cardWidthWidescreen, 10) || 400;
			const cardWidthLaptop = parseInt(wrapper.dataset.cardWidthLaptop, 10) || 380;
			const cardWidthTabletExtra = parseInt(wrapper.dataset.cardWidthTabletExtra, 10) || 360;
			const cardWidthTablet = parseInt(wrapper.dataset.cardWidthTablet, 10) || 340;
			const cardWidthMobileExtra = parseInt(wrapper.dataset.cardWidthMobileExtra, 10) || 320;
			const cardWidthMobile = parseInt(wrapper.dataset.cardWidthMobile, 10) || 300;

			// Determine current card width based on context
			if (typeof elementorFrontend !== 'undefined') {
				const currentDeviceMode = elementorFrontend.getCurrentDeviceMode();

				switch (currentDeviceMode) {
					case 'mobile':
						currentCardWidth = cardWidthMobile;
						break;
					case 'mobile_extra':
						currentCardWidth = cardWidthMobileExtra;
						break;
					case 'tablet':
						currentCardWidth = cardWidthTablet;
						break;
					case 'tablet_extra':
						currentCardWidth = cardWidthTabletExtra;
						break;
					case 'laptop':
						currentCardWidth = cardWidthLaptop;
						break;
					case 'widescreen':
						currentCardWidth = cardWidthWidescreen;
						break;
					default:
						currentCardWidth = cardWidthWidescreen;
						break;
				}
			} else {
				// Frontend: determine card width based on window width
				const width = window.innerWidth;
				if (width < 768) {
					currentCardWidth = cardWidthMobile;
				} else if (width < 880) {
					currentCardWidth = cardWidthMobileExtra;
				} else if (width < 1024) {
					currentCardWidth = cardWidthTablet;
				} else if (width < 1200) {
					currentCardWidth = cardWidthTabletExtra;
				} else if (width < 1366) {
					currentCardWidth = cardWidthLaptop;
				} else {
					currentCardWidth = cardWidthWidescreen;
				}
			}

			currentSlidesPerView = 'auto';
		} else {
			// SLIDES PER VIEW MODE (default)
			const slidesWidescreen = parseInt(wrapper.dataset.slidesWidescreen, 10) || 3;
			const slidesLaptop = parseInt(wrapper.dataset.slidesLaptop, 10) || 3;
			const slidesTabletExtra = parseInt(wrapper.dataset.slidesTabletExtra, 10) || 2;
			const slidesTablet = parseInt(wrapper.dataset.slidesTablet, 10) || 2;
			const slidesMobileExtra = parseInt(wrapper.dataset.slidesMobileExtra, 10) || 1;
			const slidesMobile = parseInt(wrapper.dataset.slidesMobile, 10) || 1;

			if (typeof elementorFrontend !== 'undefined') {
				const currentDeviceMode = elementorFrontend.getCurrentDeviceMode();

				switch (currentDeviceMode) {
					case 'mobile':
						currentSlidesPerView = slidesMobile;
						break;
					case 'mobile_extra':
						currentSlidesPerView = slidesMobileExtra;
						break;
					case 'tablet':
						currentSlidesPerView = slidesTablet;
						break;
					case 'tablet_extra':
						currentSlidesPerView = slidesTabletExtra;
						break;
					case 'laptop':
						currentSlidesPerView = slidesLaptop;
						break;
					case 'widescreen':
						currentSlidesPerView = slidesWidescreen;
						break;
					default:
						currentSlidesPerView = slidesWidescreen;
						break;
				}
			} else {
				// Frontend: determine initial view based on window width
				const width = window.innerWidth;
				if (width < 768) {
					currentSlidesPerView = slidesMobile;
				} else if (width < 880) {
					currentSlidesPerView = slidesMobileExtra;
				} else if (width < 1024) {
					currentSlidesPerView = slidesTablet;
				} else if (width < 1200) {
					currentSlidesPerView = slidesTabletExtra;
				} else if (width < 1366) {
					currentSlidesPerView = slidesLaptop;
				} else {
					currentSlidesPerView = slidesWidescreen;
				}
			}
		}

		// Apply card widths
		if (this.sizingMode === 'card_width') {
			// CARD WIDTH MODE: Apply fixed width to each card
			this.elements.$cards.each((i, card) => {
				card.style.removeProperty('width');
				card.style.setProperty('width', `${currentCardWidth}px`, 'important');
			});
		} else {
			// SLIDES PER VIEW MODE: Calculate card width based on slides per view
			const containerWidth = wrapper.offsetWidth;

			// Safeguard: Ensure container has valid width before calculating
			if (containerWidth < 100) {
				console.warn('Carousel container width invalid, deferring initialization');
				// Re-attempt initialization after a delay
				setTimeout(() => this.initCarousel(), 100);
				return;
			}

			const totalGapWidth = this.gap * (currentSlidesPerView - 1);
			const calculatedCardWidth = (containerWidth - totalGapWidth) / currentSlidesPerView;

			this.elements.$cards.each((i, card) => {
				card.style.removeProperty('width');
				card.style.setProperty('width', `${calculatedCardWidth}px`, 'important');
			});
		}

		// Build Swiper configuration
		let swiperConfig = {
			slidesPerView: currentSlidesPerView,
			slidesPerGroup: this.sizingMode === 'card_width' ? 1 : currentSlidesPerView,
			spaceBetween: 24,
			grabCursor: true,
			loop: false,
			centeredSlides: false,

			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},

			a11y: {
				enabled: true,
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
				firstSlideMessage: 'This is the first slide',
				lastSlideMessage: 'This is the last slide',
				paginationBulletMessage: 'Go to slide {{index}}',
			},

			pagination: !this.isQuote1
				? {
						el: this.elements.$pagination[0],
						clickable: true,
					}
				: false,

			navigation: {
				prevEl: this.elements.$prevArrow[0],
				nextEl: this.elements.$nextArrow[0],
			},
		};

		// Add mode-specific configuration
		if (this.sizingMode !== 'card_width') {
			// Slides Per View mode: add breakpoints and event handlers
			const slidesWidescreen = parseInt(wrapper.dataset.slidesWidescreen, 10) || 3;
			const slidesLaptop = parseInt(wrapper.dataset.slidesLaptop, 10) || 3;
			const slidesTabletExtra = parseInt(wrapper.dataset.slidesTabletExtra, 10) || 2;
			const slidesTablet = parseInt(wrapper.dataset.slidesTablet, 10) || 2;
			const slidesMobileExtra = parseInt(wrapper.dataset.slidesMobileExtra, 10) || 1;
			const slidesMobile = parseInt(wrapper.dataset.slidesMobile, 10) || 1;

			swiperConfig.on = {
				init: (swiper) => {
					this.updateCardWidths(swiper);
				},
				breakpoint: (swiper) => {
					this.updateCardWidths(swiper);
				},
				resize: (swiper) => {
					this.updateCardWidths(swiper);
				},
			};

			swiperConfig.breakpoints = {
				768: {
					slidesPerView: slidesMobile,
					slidesPerGroup: slidesMobile,
				},
				880: {
					slidesPerView: slidesMobileExtra,
					slidesPerGroup: slidesMobileExtra,
				},
				1024: {
					slidesPerView: slidesTablet,
					slidesPerGroup: slidesTablet,
				},
				1200: {
					slidesPerView: slidesTabletExtra,
					slidesPerGroup: slidesTabletExtra,
				},
				1366: {
					slidesPerView: slidesLaptop,
					slidesPerGroup: slidesLaptop,
				},
				1920: {
					slidesPerView: slidesWidescreen,
					slidesPerGroup: slidesWidescreen,
				},
			};
		}

		this.swiperInstance = new Swiper(swiperContainer, swiperConfig);

		// ADA: Announce slide changes to screen readers
		this.createLiveRegion();

		this.swiperInstance.on('slideChange', () => {
			this.announceSlideChange();
			this.updateQuote1Info();
		});
	}

	updateCardWidths(swiperInstance) {
		const currentSlides = swiperInstance.params.slidesPerView;
		const containerW = this.elements.$wrapper[0].offsetWidth;

		// Safeguard: Ensure container has valid width
		if (containerW < 100) {
			console.warn('Container width invalid during updateCardWidths, skipping');
			return;
		}

		const totalGap = this.gap * (currentSlides - 1);
		const cardWidth = (containerW - totalGap) / currentSlides;

		this.elements.$cards.each((i, card) => {
			card.style.setProperty('width', `${cardWidth}px`, 'important');
		});
	}

	createLiveRegion() {
		this.liveRegion = document.createElement('div');
		this.liveRegion.setAttribute('aria-live', 'polite');
		this.liveRegion.setAttribute('aria-atomic', 'true');
		this.liveRegion.classList.add('sr-only');
		this.elements.$wrapper[0].appendChild(this.liveRegion);
	}

	announceSlideChange() {
		const activeIndex = this.swiperInstance.activeIndex + 1;
		const total = this.swiperInstance.slides.length;
		this.liveRegion.textContent = `Showing slide ${activeIndex} of ${total}`;
	}

	updateQuote1Info() {
		// For quote-1 style: Update author info display when slide changes
		if (!this.isQuote1) return;

		const activeSlide = this.swiperInstance.slides[this.swiperInstance.activeIndex];
		if (!activeSlide) return;

		const authorName = activeSlide.dataset.authorName || '';
		const authorTitle = activeSlide.dataset.authorTitle || '';
		const authorImage = activeSlide.dataset.authorImage || '';

		if (this.elements.$cardInfoSection.length) {
			const $nameElement = this.elements.$cardInfoSection.find('.hm-carousel__card-name h6');
			const $titleElement = this.elements.$cardInfoSection.find('.hm-carousel__card-title p');
			const $imageElement = this.elements.$cardInfoSection.find('.hm-carousel__card-image');

			if ($nameElement.length) $nameElement.text(authorName);
			if ($titleElement.length) $titleElement.text(authorTitle);
			if ($imageElement.length && authorImage) {
				$imageElement.attr('src', authorImage);
				$imageElement.attr('alt', authorName);
			}
		}
	}
}

jQuery(window).on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler('hm_carousel', HoffmasterCarouselHandler);

	// Listen for device mode changes in Elementor editor
	if (typeof elementorFrontend !== 'undefined') {
		elementorFrontend.on('components:init', () => {
			let previousDeviceMode = elementorFrontend.getCurrentDeviceMode();

			setInterval(() => {
				const currentDeviceMode = elementorFrontend.getCurrentDeviceMode();
				if (currentDeviceMode !== previousDeviceMode) {
					previousDeviceMode = currentDeviceMode;

					// Re-initialize all carousels when device mode changes
					jQuery('.elementor-widget-hm_carousel').each(function () {
						const handler = elementorFrontend.elementsHandler.getHandler('hm_carousel', this);
						if (handler && handler.bindEvents) {
							handler.bindEvents();
						}
					});
				}
			}, 500);
		});
	}
});
